<template>
  <div :key="pageKey">
    <div class="page-container">
      <div class="logo-container">
        <span>
          <slot name="nav-button">
            <b-button
              v-if="isAuthenticated"
              variant="clear"
              size="sm"
              data-cy="btn-toggle-nav"
              :aria-label="navOpened ? 'Close menu' : 'Open menu'"
              @click="toggleNav"
            >
              <X v-if="navOpened" :size="24" />
              <Menu v-else :size="24" />
            </b-button>
          </slot>
        </span>
        <slot name="page-header">
          <router-link
            v-if="isAuthenticated && $route.name !== 'AccountsList'"
            :to="{ name: 'AccountsList' }"
            class="page-header-name page-header-name--link"
            >{{ accessClaims.full_name }}</router-link
          >
          <h1 v-else-if="isAuthenticated" class="page-header-name">
            {{ accessClaims.full_name }}
          </h1>
          <Logo v-else />
        </slot>
        <span><!-- spacer --></span>
      </div>
      <div class="page-content">
        <div class="v-flex grow">
          <div
            :class="[
              'card-wrapper v-flex grow',
              navOpened ? 'opened nav-overlay' : ''
            ]"
            @click="closeNav"
          >
            <b-card
              header-bg-variant="white"
              body-class="v-flex grow body-content"
              :class="['grow page-card', navOpened ? 'opened' : '']"
            >
              <template #header>
                <div class="page-card-header">
                  <slot name="card-header" />
                </div>
              </template>

              <div class="v-flex grow">
                <slot />
              </div>
            </b-card>
          </div>
          <Drawers>
            <slot name="drawers" />
          </Drawers>
        </div>
        <div v-if="navOpened" class="nav-click-capture" @click="closeNav"></div>
        <NavigationMenu
          class="main-nav"
          :class="{ opened: navOpened }"
          @closeMenu="closeNav"
        />
      </div>
      <div class="page-footer">
        <div class="powered">
          {{ $t('labels.poweredBy') }} <BlytzLogo />
          <a
            href="https://www.blytzpay.com/terms-and-conditions"
            target="_blank"
            >{{ $t('labels.TermsAndConditions') }}</a
          >
          <a href="https://www.blytzpay.com/privacy-policy" target="_blank">{{
            $t('labels.PrivacyPolicy')
          }}</a>
        </div>
      </div>
    </div>
    <div v-if="isBusy" class="spinner-container">
      <LoaderCircle />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import Logo from '@/components/Elements/Logo/Logo.vue'
import BlytzLogo from '@/components/Elements/Logo/BlytzLogo.vue'
import NavigationMenu from '@/components/NavigationMenu/NavigationMenu.vue'
import { debounce } from 'lodash-es'
import Drawers from '@/components/Elements/Drawer/Drawers.vue'
import { mapGetters, mapMutations } from 'vuex'
import { Menu, X, LoaderCircle } from 'lucide-vue'

@Component({
  components: {
    BlytzLogo,
    Logo,
    NavigationMenu,
    Drawers,
    Menu,
    X,
    LoaderCircle
  },
  methods: {
    ...mapMutations(['setBusyCount'])
  },
  computed: {
    ...mapGetters(['accessClaims', 'busyCount', 'isAuthenticated'])
  }
})
export default class Page extends Vue {
  navOpened = false
  isBusy = false
  setBusyCount!: Function
  busyCount: Function
  pageKey = 0

  mounted() {
    const pageContent = document.querySelector('.page-content')
    pageContent.addEventListener('scroll', (e: any) => {
      // automatically close if content is dragged far enough to left and reset
      if (e.target.scrollLeft > 320) {
        this.navOpened = false
        this.pageKey + 1
      }
    })
  }

  toggleNav() {
    this.navOpened = !this.navOpened
  }

  busyChecker() {
    this.isBusy = Boolean(this.busyCount())
  }
  debouncedBusyChecker = debounce(this.busyChecker, 200, { trailing: true })
  @Watch('busyCount')
  busyCountChanged() {
    this.debouncedBusyChecker()
  }
  get showNav(): boolean {
    const showNav = this.$route.meta.hideNav

    // NOTE: preserves prior runtime behavior. The original guard
    // (`typeof showNav !== undefined`) was always true, so this always
    // returned meta.hideNav. Latent naming/logic bug left for a follow-up.
    return showNav
  }

  closeNav() {
    this.navOpened = false
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/PageStyles.scss';
</style>
